import { API_URL } from "../../../utils/runtimeEnv";
import { ROLE } from "../../../utils/Constants";
import { getLocalKey, getUser } from "../../../utils/commonfunction";

const CHATBOT_STREAM_PATH = "restaurant/chatbot/stream";
const CHATBOT_SESSIONS_PATH = "restaurant/chatbot/sessions";
const CHATBOT_REFERENCE_IMAGE_PATH = "restaurant/chatbot/reference-image";
const CHATBOT_REFERENCES_PATH = "restaurant/chatbot/references";
const CHATBOT_STARRED_IMAGES_PATH = "restaurant/chatbot/starred-images";
const CHATBOT_LINE_DELIVERY_STATUS_PATH = "restaurant/chatbot/line-delivery-status";
const CHATBOT_SEND_TO_LINE_PATH = "restaurant/chatbot/send-to-line";
const CHATBOT_SESSION_HEADER = "X-Chatbot-Session-Id";
const CHATBOT_REFERENCE_IMAGE_FIELD = "image";
const DEFAULT_LANGUAGE = "en";
const DATA_STREAM_PREFIX = "data:";
const DATA_STREAM_DONE = "[DONE]";
const SSE_DONE_LINE = `${DATA_STREAM_PREFIX} ${DATA_STREAM_DONE}`;
const VERCEL_DATA_STREAM_TEXT_PREFIX = "0:";
const STREAM_EVENT_TYPE_TEXT_DELTA = "text-delta";
const STREAM_EVENT_TYPE_TEXT = "text";
const STREAM_EVENT_TYPE_TOOL_INPUT_START = "tool-input-start";
const STREAM_EVENT_TYPE_ERROR = "error";
const GENERATE_MARKETING_BANNER_TOOL = "generate_marketing_banner_image";
const EDIT_MARKETING_BANNER_TOOL = "edit_marketing_banner_image";
const CONTENT_TYPE_HEADER = "Content-Type";
const JSON_CONTENT_TYPE = "application/json";
const LANGUAGE_STORAGE_KEY = "system_language";
const HTTP_METHOD_POST = "POST";
const HTTP_METHOD_GET = "GET";
const EMPTY_DISPLAY_MESSAGE = "";
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
const DEFAULT_SESSION_PAGE = 1;
const DEFAULT_SESSION_PAGE_SIZE = 20;

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  reference_image_urls?: string[];
}

export interface StreamChatbotRequest {
  messages: ChatMessage[];
  sessionId?: string | null;
  signal: AbortSignal;
  onDelta: (delta: string) => void;
  onStatus?: (status: ChatbotStreamStatus) => void;
}

export type ChatbotStreamStatus = "thinking" | "generating_image" | "editing_image";

export interface StreamChatbotResponse {
  sessionId: string | null;
  text: string;
}

export interface ChatbotSessionSummary {
  _id: string;
  title: string | null;
  started_at: string;
  last_message_at: string;
  expires_at: string;
  turn_count: number;
  status: string;
}

export interface ChatbotSessionListResponse {
  data: ChatbotSessionSummary[];
  page: number;
  per_page: number;
  total_records: number;
  total_pages: number;
}

export interface ChatbotTurn {
  _id: string;
  session_id: string;
  turn_index: number;
  question: {
    content: string;
    reference_images?: ChatbotReferenceImage[];
  };
  answer: {
    content: string;
    tool_calls?: Array<{ tool_name: string; tool_type?: string }>;
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    cost_usd?: number;
    completed_at?: string | null;
  };
  createdAt?: string;
}

export interface ChatbotSessionTurnsResponse {
  session: ChatbotSessionSummary;
  turns: ChatbotTurn[];
}

export interface ChatbotUsageWindowStatus {
  key: string;
  tokens_used: number;
  token_limit: number;
  image_generations_used: number;
  image_generation_limit: number;
  cost_usd: number;
  cost_limit_usd: number;
  reset_at: string | null;
}

export interface ChatbotUsageStatus {
  usage_windows: ChatbotUsageWindowStatus[];
}

export interface ChatbotReferenceImage {
  file_name: string;
  image_url: string;
}

export interface ChatbotPosterReference extends ChatbotReferenceImage {
  _id: string;
  enabled: boolean;
  display_order: number;
}

export interface ChatbotStarredImage {
  id: string;
  image_url: string;
  title: string;
  session_id: string;
  turn_id: string;
  session_title: string;
  source_tool_type: string;
  starred_at: string;
}

export interface ChatbotLineDeliveryStatus {
  available: boolean;
  line_enabled: boolean;
  has_access_token: boolean;
  reason: string | null;
}

export const getChatbotPosterReferences = async (): Promise<ChatbotPosterReference[]> => {
  const response = await fetch(getApiUrl(CHATBOT_REFERENCES_PATH), { headers: getAuthHeaders(false) });
  if (!response.ok) throw new ChatbotRequestError("", response.status);
  return ((await response.json()) as ApiResponse<ChatbotPosterReference[]>).data || [];
};

export const uploadChatbotPosterReference = async (file: File): Promise<ChatbotPosterReference> => {
  const body = new FormData();
  body.append(CHATBOT_REFERENCE_IMAGE_FIELD, file);
  const response = await fetch(getApiUrl(CHATBOT_REFERENCES_PATH), {
    method: HTTP_METHOD_POST, headers: getAuthHeaders(false), body,
  });
  if (!response.ok) throw new ChatbotRequestError("", response.status);
  return ((await response.json()) as ApiResponse<ChatbotPosterReference>).data;
};

export const updateChatbotPosterReference = async (
  id: string,
  values: Partial<Pick<ChatbotPosterReference, "display_order">>,
): Promise<ChatbotPosterReference> => {
  const response = await fetch(getApiUrl(`${CHATBOT_REFERENCES_PATH}/${id}`), {
    method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(values),
  });
  if (!response.ok) throw new ChatbotRequestError("", response.status);
  return ((await response.json()) as ApiResponse<ChatbotPosterReference>).data;
};

export const deleteChatbotPosterReference = async (id: string): Promise<void> => {
  const response = await fetch(getApiUrl(`${CHATBOT_REFERENCES_PATH}/${id}`), {
    method: "DELETE", headers: getAuthHeaders(false),
  });
  if (!response.ok) throw new ChatbotRequestError("", response.status);
};

export const getChatbotStarredImages = async (): Promise<ChatbotStarredImage[]> => {
  const response = await fetch(getApiUrl(CHATBOT_STARRED_IMAGES_PATH), {
    method: HTTP_METHOD_GET,
    headers: getAuthHeaders(),
  });
  return parseJsonResponse<ChatbotStarredImage[]>(response);
};

export const createChatbotStarredImage = async (values: {
  session_id: string;
  turn_id: string;
  image_url: string;
  title?: string;
}): Promise<ChatbotStarredImage> => {
  const response = await fetch(getApiUrl(CHATBOT_STARRED_IMAGES_PATH), {
    method: HTTP_METHOD_POST,
    headers: getAuthHeaders(),
    body: JSON.stringify(values),
  });
  return parseJsonResponse<ChatbotStarredImage>(response);
};

export const deleteChatbotStarredImage = async (id: string): Promise<void> => {
  const response = await fetch(getApiUrl(`${CHATBOT_STARRED_IMAGES_PATH}/${id}`), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new ChatbotRequestError(await getErrorMessage(response), response.status);
};

export const getChatbotLineDeliveryStatus = async (): Promise<ChatbotLineDeliveryStatus> => {
  const response = await fetch(getApiUrl(CHATBOT_LINE_DELIVERY_STATUS_PATH), {
    method: HTTP_METHOD_GET,
    headers: getAuthHeaders(),
  });

  return parseJsonResponse<ChatbotLineDeliveryStatus>(response);
};

export const sendChatbotImageToLine = async (values: {
  image_url: string;
  title?: string;
  session_id: string;
  turn_id: string;
}): Promise<{ sent: true; image_url: string; title: string }> => {
  const response = await fetch(getApiUrl(CHATBOT_SEND_TO_LINE_PATH), {
    method: HTTP_METHOD_POST,
    headers: getAuthHeaders(),
    body: JSON.stringify(values),
  });

  return parseJsonResponse<{ sent: true; image_url: string; title: string }>(response);
};

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface StreamEvent {
  type: string;
  delta?: string;
  text?: string;
  toolName?: string;
  errorText?: string;
}

interface ParsedStreamLine {
  delta: string;
  status?: ChatbotStreamStatus;
  error?: string;
}

class ChatbotRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

class ChatbotTransportError extends Error {
  constructor() {
    super(EMPTY_DISPLAY_MESSAGE);
  }
}

const getChatbotStreamUrl = (): string => {
  const baseUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
  return `${baseUrl}/${CHATBOT_STREAM_PATH}`;
};

const getApiUrl = (path: string): string => {
  const baseUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
  return `${baseUrl}/${path}`;
};

const getAuthHeaders = (includeJsonContentType = true): Record<string, string> => {
  const restaurantUser = getUser(ROLE.RESTAURANT) as { token?: string } | null;
  const headers: Record<string, string> = {
    language: (getLocalKey(LANGUAGE_STORAGE_KEY) as string | null) || DEFAULT_LANGUAGE,
  };

  if (includeJsonContentType) {
    headers[CONTENT_TYPE_HEADER] = JSON_CONTENT_TYPE;
  }

  if (restaurantUser?.token) {
    headers.Authorization = `Bearer ${restaurantUser.token}`;
  }

  return headers;
};

const parseVercelDataStreamLine = (line: string): string => {
  if (!line.startsWith(VERCEL_DATA_STREAM_TEXT_PREFIX)) return "";
  try {
    return JSON.parse(line.slice(VERCEL_DATA_STREAM_TEXT_PREFIX.length)) as string;
  } catch {
    return "";
  }
};

const parseStreamLine = (line: string): ParsedStreamLine => {
  const trimmedLine = line.trim();
  if (!trimmedLine || trimmedLine === SSE_DONE_LINE) return { delta: "" };

  const normalizedLine = trimmedLine.startsWith(DATA_STREAM_PREFIX)
    ? trimmedLine.slice(DATA_STREAM_PREFIX.length).trim()
    : trimmedLine;

  if (!normalizedLine || normalizedLine === DATA_STREAM_DONE) return { delta: "" };

  const vercelDataStreamDelta = parseVercelDataStreamLine(normalizedLine);
  if (vercelDataStreamDelta) return { delta: vercelDataStreamDelta };

  try {
    const streamEvent = JSON.parse(normalizedLine) as StreamEvent;
    if (streamEvent?.type === STREAM_EVENT_TYPE_TEXT_DELTA && streamEvent?.delta) {
      return { delta: streamEvent.delta };
    }
    if (streamEvent?.type === STREAM_EVENT_TYPE_TEXT && streamEvent?.text) {
      return { delta: streamEvent.text };
    }
    if (streamEvent?.type === STREAM_EVENT_TYPE_TOOL_INPUT_START) {
      if (streamEvent.toolName === GENERATE_MARKETING_BANNER_TOOL) {
        return { delta: "", status: "generating_image" };
      }
      if (streamEvent.toolName === EDIT_MARKETING_BANNER_TOOL) {
        return { delta: "", status: "editing_image" };
      }
    }
    if (streamEvent?.type === STREAM_EVENT_TYPE_ERROR) {
      return { delta: "", error: streamEvent.errorText || EMPTY_DISPLAY_MESSAGE };
    }
  } catch {
    return { delta: "" };
  }

  return { delta: "" };
};

const getErrorMessage = async (response: Response): Promise<string> => {
  try {
    const errorData = (await response.json()) as { message?: string };
    return errorData?.message || EMPTY_DISPLAY_MESSAGE;
  } catch {
    return EMPTY_DISPLAY_MESSAGE;
  }
};

export const streamChatbotResponse = async ({
  messages,
  sessionId = "",
  signal,
  onDelta,
  onStatus,
}: StreamChatbotRequest): Promise<StreamChatbotResponse> => {
  const response = await fetch(getChatbotStreamUrl(), {
    method: HTTP_METHOD_POST,
    headers: getAuthHeaders(),
    signal,
    body: JSON.stringify({
      messages,
      ...(sessionId ? { session_id: sessionId } : {}),
    }),
  });

  if (!response.ok) {
    throw new ChatbotRequestError(await getErrorMessage(response), response.status);
  }

  if (!response.body) {
    throw new ChatbotTransportError();
  }

  const nextSessionId = response.headers.get(CHATBOT_SESSION_HEADER);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";

  const emitDelta = (delta: string): void => {
    for (const character of delta) {
      fullText += character;
      onDelta(character);
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const { delta, status, error } = parseStreamLine(line);
      if (error) throw new ChatbotRequestError(error, HTTP_STATUS_INTERNAL_SERVER_ERROR);
      if (status) onStatus?.(status);
      if (delta) emitDelta(delta);
    }
  }

  const { delta: remainingDelta, status: remainingStatus, error: remainingError } = parseStreamLine(buffer);
  if (remainingError) throw new ChatbotRequestError(remainingError, HTTP_STATUS_INTERNAL_SERVER_ERROR);
  if (remainingStatus) onStatus?.(remainingStatus);
  if (remainingDelta) emitDelta(remainingDelta);

  return { sessionId: nextSessionId, text: fullText };
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new ChatbotRequestError(await getErrorMessage(response), response.status);
  }

  const payload = (await response.json()) as ApiResponse<T>;
  if (!payload.success) {
    throw new ChatbotRequestError(payload.message || EMPTY_DISPLAY_MESSAGE, response.status);
  }

  return payload.data;
};

export const getChatbotSessions = async (
  page = DEFAULT_SESSION_PAGE,
  perPage = DEFAULT_SESSION_PAGE_SIZE
): Promise<ChatbotSessionListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });

  const response = await fetch(`${getApiUrl(CHATBOT_SESSIONS_PATH)}?${params.toString()}`, {
    method: HTTP_METHOD_GET,
    headers: getAuthHeaders(),
  });

  return parseJsonResponse<ChatbotSessionListResponse>(response);
};

export const getChatbotSessionTurns = async (
  sessionId: string
): Promise<ChatbotSessionTurnsResponse> => {
  const response = await fetch(`${getApiUrl(`${CHATBOT_SESSIONS_PATH}/${sessionId}/turns`)}`, {
    method: HTTP_METHOD_GET,
    headers: getAuthHeaders(),
  });

  return parseJsonResponse<ChatbotSessionTurnsResponse>(response);
};

export const deleteChatbotSession = async (sessionId: string): Promise<void> => {
  const response = await fetch(getApiUrl(`${CHATBOT_SESSIONS_PATH}/${sessionId}`), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new ChatbotRequestError(await getErrorMessage(response), response.status);
  }
};

export const getChatbotUsageStatus = async (): Promise<ChatbotUsageStatus> => {
  const response = await fetch(getApiUrl('restaurant/chatbot/usage-status'), {
    method: HTTP_METHOD_GET,
    headers: getAuthHeaders(),
  });

  return parseJsonResponse<ChatbotUsageStatus>(response);
};

export const uploadChatbotReferenceImage = async (
  file: File
): Promise<ChatbotReferenceImage> => {
  const body = new FormData();
  body.append(CHATBOT_REFERENCE_IMAGE_FIELD, file);

  const response = await fetch(getApiUrl(CHATBOT_REFERENCE_IMAGE_PATH), {
    method: HTTP_METHOD_POST,
    headers: getAuthHeaders(false),
    body,
  });

  return parseJsonResponse<ChatbotReferenceImage>(response);
};
